/** TanStack Query hooks for storage folders and folder-scoped file operations */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE, ApiError } from '../lib/api-client'
import { getCreditErrorGuidance } from '../lib/credit-error-guidance'
import { appendUploadDescription } from '../lib/upload-description'

/**
 * JSON-serialized form of StorageFolderTree from @agentwiki/shared.
 * Dates are ISO strings (not Date objects) since they arrive via JSON API.
 */
export interface StorageFolderNode {
  id: string
  tenantId: string
  parentId: string | null
  name: string
  slug: string
  position: number
  createdBy: string
  createdAt: string
  updatedAt: string
  fileCount: number
  children: StorageFolderNode[]
}

/**
 * JSON-serialized form of Upload from @agentwiki/shared.
 * Dates are ISO strings (not Date objects) since they arrive via JSON API.
 */
export interface StorageFile {
  id: string
  fileKey: string
  filename: string
  contentType: string
  sizeBytes: number
  folderId: string | null
  extractionStatus: string | null // pending | processing | completed | failed | unsupported
  extractionMethod: string | null // vision | text | unsupported | null
  summary: string | null
  description: string | null
  lastSummaryAt: number | null // epoch ms (matches API serialization)
  createdAt: string
}

// ── Folder tree ───────────────────────────────────────────────────────────────

export function useStorageFolderTree() {
  return useQuery<{ folders: StorageFolderNode[] }>({
    queryKey: ['storage-folders'],
    queryFn: () => apiClient.get<{ folders: StorageFolderNode[] }>('/api/storage-folders'),
  })
}

export function useCreateStorageFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; parentId?: string | null }) =>
      apiClient.post<{ id: string; name: string }>('/api/storage-folders', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-folders'] }),
  })
}

export function useUpdateStorageFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      name?: string
      parentId?: string | null
      position?: number
    }) => apiClient.put<{ id: string }>(`/api/storage-folders/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-folders'] }),
  })
}

export function useDeleteStorageFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: boolean }>(`/api/storage-folders/${id}`),
    onSuccess: () => {
      // Invalidate both folders and all storage file queries (files reassigned to root)
      qc.invalidateQueries({ queryKey: ['storage-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-files'] })
    },
  })
}

// ── Folder-scoped file listing ────────────────────────────────────────────────

const FILES_PAGE_SIZE = 50

/** folderId=null -> root files, folderId=undefined -> all files. Infinite scroll pagination. */
export function useStorageFiles(folderId?: string | null) {
  const folderParam = folderId === null ? 'root' : folderId !== undefined ? folderId : undefined

  return useInfiniteQuery<{ files: StorageFile[]; hasMore: boolean }>({
    queryKey: ['storage-files', folderId],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams()
      if (folderParam !== undefined) params.set('folderId', folderParam)
      params.set('limit', String(FILES_PAGE_SIZE))
      params.set('offset', String(pageParam))
      return apiClient.get<{ files: StorageFile[]; hasMore: boolean }>(`/api/uploads?${params}`)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.reduce((sum, p) => sum + p.files.length, 0) : undefined,
  })
}

// ── File upload into a specific folder (with progress tracking) ─────────────

import { useCallback } from 'react'
import { useAppStore } from '../stores/app-store'

/** Send XHR upload with progress tracking */
function sendXhr(
  url: string,
  formData: FormData,
  onProgress: (pct: number) => void,
): Promise<{ status: number; data: unknown }> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    })
    xhr.addEventListener('load', () => {
      let data: unknown = null
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        data = null
      }
      resolve({ status: xhr.status, data })
    })
    xhr.addEventListener('error', () => resolve({ status: 0, data: null }))
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.send(formData)
  })
}

/** Returns an upload function that tracks progress in the global upload queue */
export function useUploadToFolder() {
  const { addToUploadQueue, updateUploadProgress, updateUploadStatus, removeFromUploadQueue } =
    useAppStore()
  const qc = useQueryClient()

  return useCallback(
    async (file: File, folderId?: string | null) => {
      const queueId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      addToUploadQueue([{ id: queueId, file }])
      updateUploadStatus(queueId, 'uploading')

      const formData = new FormData()
      formData.append('file', file)
      appendUploadDescription(formData, file, 'storage-folder')
      if (folderId) formData.append('folderId', folderId)

      const { status, data } = await sendXhr(`${API_BASE}/api/uploads`, formData, (pct) =>
        updateUploadProgress(queueId, pct),
      )

      if (status >= 200 && status < 300) {
        updateUploadStatus(queueId, 'complete')
        qc.invalidateQueries({ queryKey: ['storage-files', folderId ?? null] })
        qc.invalidateQueries({ queryKey: ['storage-folders'] })
        qc.invalidateQueries({ queryKey: ['uploads'] })
        setTimeout(() => removeFromUploadQueue(queueId), 3000)
      } else {
        const guidance = getCreditErrorGuidance(new ApiError(status, 'Upload failed', data))
        updateUploadStatus(
          queueId,
          'error',
          guidance
            ? `${guidance.description} ${guidance.actionLabel}: ${guidance.actionHref}`
            : status === 0
              ? 'Network error'
              : `Upload failed (${status})`,
        )
      }
    },
    [addToUploadQueue, updateUploadProgress, updateUploadStatus, removeFromUploadQueue, qc],
  )
}

// ── Move a single file to a folder ───────────────────────────────────────────

export function useMoveUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      apiClient.put<{ ok: boolean }>(`/api/uploads/${id}/move`, { folderId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['storage-files'] })
      qc.invalidateQueries({ queryKey: ['uploads'] })
    },
  })
}

// when multi-select UI is implemented. Backend endpoints are ready.
